import React, { useContext, useMemo, useState } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { AuthContext } from '../../../../../context/auth.context'
import { useApiListLoginProvider } from '../../../../../api-new/state/auth'
import { IconProvider } from '../../../../../shared/icon/icon'
import { useLoad } from '../../../../../shared/hooks/load'
import { FleetPage, FleetSection, EmptyState } from '../../../../../shared/sso-workspace/fleet-page'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { formatDateTime, serverMessage } from '../../../../../shared/sso-workspace/format'
import '../../../../../shared/sso-consent/sso-consent.scss'

const nameOf = row => row.displayName || row.provider || 'This connection'

// When, where and on what: the last sign-in when the server knows it, else
// the first ("First signed in"). Place and device come from the server
// already in words ("" when unknown, and then left out); a server from
// before them sends neither, so the place falls back to the stored one and
// the raw device is never printed.
export const signInLine = row => {
  const when = row.lastSignedIn
    ? `Last signed in ${formatDateTime(row.lastSignedIn)}`
    : row.authorized && `First signed in ${formatDateTime(row.authorized)}`
  const place = row.place !== undefined ? row.place : row.ipLocation
  return [when, place, row.device].filter(Boolean).join(' · ')
}

// What Disconnect does, in the confirmation. It only forgets the record: a
// public sign-in method is not blocked by it -- but the next sign-in with
// it meets an existing account it isn't connected to, so auth asks the
// person to confirm the account is theirs once more (SSO link
// confirmation) -- and a stale link (a connection the person's email domain
// is no longer bound to) wasn't a way in anyway.
const disconnectBody = row => {
  const name = nameOf(row)
  const what = row.public
    ? `EducationXR forgets this connection. The next time you sign in with ${name}, we'll ask you to confirm it's your account.`
    : `EducationXR forgets this connection. Your email's domain no longer signs in with ${name}.`
  return `${what} You stay signed in now, and your password, if you have one, still works.`
}

// The signed-in person's own sign-in connections (audit P4, section 4;
// owner ruling 2026-10-07): each connection they have signed in with, when
// and where. The server decides what the person may do with each row: a
// connection their organization manages says "Managed by <organization>"
// and has no Disconnect; Disconnect appears only on rows the server marks
// `removable`, behind a confirmation. The server still refuses removing the
// last way to sign in (409), and the dialog shows its sentence.
export const LoginProvider = () => {
  const authContext = useContext(AuthContext)
  const apiListLoginProvider = useApiListLoginProvider()

  const loginID = useMemo(() => authContext.state.jwtClaims.loginID, [authContext.state.jwtClaims])
  const email = useMemo(() => authContext.state.jwtClaims.email, [authContext.state.jwtClaims])

  const [rows, setRows] = useState(undefined)
  const [disconnecting, setDisconnecting] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('')

  const load = async () => {
    if (!loginID) return
    try { setRows((await apiListLoginProvider.get(loginID)) || []) } catch (err) { setRows(null) }
  }

  useLoad(() => load(), [loginID])

  const handleDisconnect = async () => {
    setBusy(true)
    setError(null)
    try {
      await apiListLoginProvider.delete(`${disconnecting.id}`)
    } catch (err) {
      setError(serverMessage(err, `${nameOf(disconnecting)} couldn't be disconnected.`))
      setBusy(false)
      return
    }
    setBusy(false)
    setStatus(`Disconnected ${nameOf(disconnecting)}.`)
    setDisconnecting(null)
    await load()
  }

  return (
    <BreadcrumbView Icon={IconProvider} text="Sign-in connections">
      <FleetPage
        title="Your sign-in connections"
        purpose={`The work or organization accounts you've used to sign in to EducationXR${email ? ` as ${email}` : ''}.`}>
        <FleetSection id="connections" title="Connected accounts">
          {status && <p className="sw-status text-start mb-2" role="status">{status}</p>}
          {rows === undefined && <p className="sw-hint m-0" role="status">Loading…</p>}
          {rows === null && <p className="text-danger small m-0" role="alert">Your connections couldn't be loaded. Refresh the page to try again.</p>}
          {Array.isArray(rows) && rows.length === 0 &&
            <EmptyState>You haven't signed in with a work or organization account yet. When you do, it appears here.</EmptyState>}
          {Array.isArray(rows) && rows.length > 0 &&
            <ul className="sc-rows">
              {rows.map(row =>
                <li key={row.id} className="sc-row" data-testid={`login-provider-${row.id}`}>
                  <div className="sc-row-main">
                    <span className="fw-semibold">{nameOf(row)}</span>
                    {row.managed && <span className="sw-hint">Managed by {row.managedBy || 'your organization'}</span>}
                    <span className="sw-hint">{signInLine(row)}</span>
                  </div>
                  {row.removable &&
                    <button type="button" className="btn btn-sm sw-btn" onClick={() => { setError(null); setDisconnecting(row) }}>
                      Disconnect<span className="visually-hidden"> {nameOf(row)}</span>
                    </button>}
                </li>)}
            </ul>}
        </FleetSection>
      </FleetPage>

      <ConfirmDialog
        open={Boolean(disconnecting)}
        title={disconnecting ? `Disconnect ${nameOf(disconnecting)}?` : ''}
        body={disconnecting ? disconnectBody(disconnecting) : ''}
        confirmText="Disconnect"
        busy={busy}
        error={error}
        onConfirm={handleDisconnect}
        onCancel={() => setDisconnecting(null)}
      />
    </BreadcrumbView>
  )
}
